import { UserType } from '../models/user';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, map, finalize, filter, take } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import { Token } from '../models/token';
import { BaseService } from './base.service';
import { environment } from 'src/environments/environment';
import { TimeService } from './time.service';
import { AllActionTypes, IPermissions } from '../models/premissions.model';
import { rolesWithPermissions } from '../data/roles-with-permissions.data';

@Injectable({
  providedIn: 'root'
})
export class AccountService extends BaseService {
  public static readonly defaultTimeZoneId = 'W. Europe Standard Time';

  private router = inject(Router);
  private http = inject(HttpClient);
  private timeService = inject(TimeService);

  private tokenSubject!: BehaviorSubject<Token>;
  public token!: Observable<Token>;
  public isSysadmin = false;
  public userType: UserType = UserType.CustomerUser;
  public companyId = -1;
  public timeZoneId = AccountService.defaultTimeZoneId;
  private rerechIsOngoing = false;
  private needToResetPassword = false;

  subscription: Subscription | undefined;

  constructor() {
    super();

    this.tokenSubject = new BehaviorSubject<Token>(<Token>{});
    this.token = this.tokenSubject.asObservable();
    this.token.subscribe(x => this.setTokenInfo(x.token));

    const source = this.timeService.getIntervalInH(1);
    this.subscription = source.subscribe(async val => this.refresh(this.tokenValue).subscribe());
  }

  loadToken() {
    return new Promise<void>((resolve, reject) => {
      const token = localStorage.getItem('token');
      if (!token || token == 'undefined') {
        resolve();
        return;
      }

      const t = JSON.parse(token);
      this.tokenSubject.next(t);
      if (this.ChekisLoggdIn(t)) {

        resolve();
      } else {
        this.refresh(t).subscribe({ next: _ => resolve(), error: _ => resolve() });
      }
    });

  }

  //Todo add logout time

  public get tokenValue(): Token {
    return this.tokenSubject.value;
  }

  isLogdIn(): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiUrl}/api/User/IsLogdIn`)
      .pipe(
        catchError(this.handleError<boolean>('isLogdIn'))
      );
  }

  /** Anonymous: triggers reset mail when identifier matches an account (backend does not reveal existence). */
  requestPasswordReset(body: { email?: string; personNr?: string }): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiUrl}/api/User/RequestResetOfPassword`, body)
      .pipe(catchError(this.handleError<boolean>('requestPasswordReset')));
  }

  validateResetToken(secureString: string): Observable<boolean> {
    const encoded = encodeURIComponent(secureString);
    return this.http
      .post<boolean>(`${environment.apiUrl}/api/User/ResetPasswordValid/${encoded}`, {})
      .pipe(catchError(this.handleError<boolean>('validateResetToken')));
  }

  completePasswordReset(secureString: string, newPassword: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${environment.apiUrl}/api/User/ResetPassword`, { secureString, newPassword })
      .pipe(catchError(this.handleError<boolean>('completePasswordReset')));
  }

  login(username: string, password: string): Observable<void> {
    return this.http.post<Token>(`${environment.apiUrl}/api/User/LogIn`, { username, password })
      .pipe(
        catchError(this.handleError<Token>('GetUser')),
        map(user => {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem('token', JSON.stringify(user));
          this.tokenSubject.next(user);
        })
      );
  }

  changePassword(newPassword: string) {
    throw new Error('Method not implemented.');
  }

  refresh(token: Token): Observable<void> {
    if (!token || !token.token) {
      console.log('no token');
      this.logout();
      return of();
    }

    if (this.userType != UserType.PunchClockUser) {
      return of();
    }

    if (this.rerechIsOngoing) {
      return this.waitForOngoingRefresh();
    }

    return this.executeRefresh(token);
  }

  private waitForOngoingRefresh(): Observable<void> {
    return this.timeService.getIntervalInS(1).pipe(
      filter(() => !this.rerechIsOngoing),
      take(1),
      map(() => void 0)
    );
  }

  private executeRefresh(token: Token): Observable<void> {
    this.rerechIsOngoing = true;
    return this.http.post<Token>(`${environment.apiUrl}/api/User/refresh`, token)
      .pipe(
        catchError(this.handleError<Token>('GetUser')),
        map(user => {
          localStorage.setItem('token', JSON.stringify(user));
          this.tokenSubject.next(user);

          if (!this.ChekisLoggdIn(user)) {
            this.logout();
          }
        }),
        finalize(() => {
          this.rerechIsOngoing = false;
        })
      );
  }

  logout(returnUrl?: string, sessionExpired = false) {
    localStorage.removeItem('token');
    this.tokenSubject.next(<Token>{});
    this.timeZoneId = AccountService.defaultTimeZoneId;
    const queryParams: { returnUrl?: string; reason?: string } = {};
    if (returnUrl) queryParams.returnUrl = returnUrl;
    if (sessionExpired) queryParams.reason = 'sessionExpired';
    this.router.navigate(['/account/login'], { queryParams: Object.keys(queryParams).length ? queryParams : {} });
  }

  setTokenInfo(tokenStr: string) {
    if (!tokenStr) {
      return;
    }

    const decoded: any = jwtDecode(tokenStr);
    console.log(decoded);
    this.setNeedToResetPassword(decoded);
    this.applyRoleFromToken(decoded);
    this.setTimeZoneId(decoded);
  }

  private applyRoleFromToken(decoded: any): void {
    const role = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    switch (role) {
      case 'SysAdmin':
        this.isSysadmin = true;
        this.userType = UserType.SysAdmin;
        return;
      case 'CustomerUser':
        this.setRoleWithCompany(decoded, UserType.CustomerUser);
        return;
      case 'TimeReportingUser':
        this.setRoleWithCompany(decoded, UserType.TimeReportingUser);
        return;
      case 'PunchClockUser':
        this.setRoleWithCompany(decoded, UserType.PunchClockUser);
        return;
      case 'DeveloperUser':
        this.setRoleWithCompany(decoded, UserType.DeveloperUser);
        return;
      default:
        return;
    }
  }

  private setRoleWithCompany(decoded: any, userType: UserType): void {
    this.isSysadmin = false;
    this.setCompanyId(decoded);
    this.userType = userType;
  }

  setCompanyId(decoded: any): void {
    const companyIdText = decoded['Company_Id'];
    console.log(companyIdText);
    if (companyIdText) {
      this.companyId = parseInt(companyIdText);
    }
  }

  private setTimeZoneId(decoded: any): void {
    const timeZoneText = decoded['TimeZoneId'];
    if (typeof timeZoneText === 'string' && timeZoneText.trim().length > 0) {
      this.timeZoneId = timeZoneText.trim();
      return;
    }
    this.timeZoneId = AccountService.defaultTimeZoneId;
  }

  setNeedToResetPassword(decoded: any): void {
    const needToResetPasswordText = decoded['RequierNewPassword'];
    console.log(needToResetPasswordText);
    if (needToResetPasswordText) {
      this.needToResetPassword = JSON.parse(needToResetPasswordText);
    }
  }

  navigateToDefultPageForUserType(): void {
    if(this.needToResetPassword){
      this.router.navigateByUrl('/account/reset-password');
      return;
    }

    switch (this.userType) {
      case UserType.SysAdmin:
        this.router.navigateByUrl('');
        break;
      case UserType.CustomerUser:
        this.router.navigateByUrl('');
        break;
      case UserType.TimeReportingUser:
        this.router.navigateByUrl('/time/min');
        break;
      case UserType.PunchClockUser:
        this.router.navigateByUrl('/time/plats');
        break;
      case UserType.DeveloperUser:
        this.router.navigateByUrl('/project/developer');
        break;
    }
  }

  public existValidToken(): boolean {
    const token = this.tokenValue;
    if (token == null || token == undefined) return false;
    return this.ChekisLoggdIn(token);
  }

  private ChekisLoggdIn(token: Token): boolean {
    if (token && token.experation) {
      const exDate = new Date(token.experation);
      console.log(exDate);
      if (exDate.getTime() > Date.now()) {
        return true;
      }
    }
    return false;
  }

  hasPermission(entity: keyof IPermissions, action: AllActionTypes): boolean {
    if (!this.userType) {
      return false;
    }

    const rolePermissions = rolesWithPermissions[this.userType];
    if (!rolePermissions) {
      //console.log('rolePermissions', rolePermissions);
      return false;
    }

    const entityPermissions = rolePermissions[entity];
    if (!entityPermissions) {
      //console.log('entityPermissions', entityPermissions);
      return false;
    }

    const actionPermission = entityPermissions[action];
    if (typeof actionPermission === 'boolean') {
      //console.log('actionPermission', actionPermission);
      return actionPermission;
    }

    // if (typeof actionPermission === 'function') {
    //   const user: User = { id: 1, userType: this.userType, name: 'John Doe' }; // Example user, replace with actual user data
    //   return actionPermission(user, permissions[entity].dataType);
    // }
    console.log('Ended chek woitoud fing');
    return false;
  }

}
